<template>
	<OnboardingFormStep :commit="commit" :next-button="nextButtonText" @navigate:before="emailError = null">

		<!-- Notice -->
		<template #notice-top v-if="candidateCount > 0">
			<div class="notice-green mx-auto text-center">
				<p class="fs-14 lh-20">
					Good news! <strong>{{ formatNumber(candidateCount) }} {{ candidateCount === 1 ? 'photographer is' : 'photographers are' }} available for your job.</strong> Create a free account to view your search results.
				</p>
			</div>
		</template>

		<template #top>
			<p class="center">
				<router-link :to="{ name: $routes.Login.name }">Already have an account? Sign in here.</router-link>
			</p>
		</template>

		<template #heading>
			<h1 class="onboarding-step-heading">Let's set up a free {{ planName.toLowerCase() }} account...</h1>
		</template>

		<template #header v-if="plan && plan.canBeHired">
			<p>
				You'll be able to receive job offers immediately, and you'll only start paying when you're
				ready to accept an offer.
			</p>
		</template>
		
		<template #default>
			<fieldset class="form-section" aria-label="About You">
				<div class="form-row">
					<TextField v-model="firstName" type="first-name" required>First Name</TextField>
					<TextField v-model="lastName" type="last-name" required>Last Name</TextField>
				</div>
				<div class="form-row">

					<TextField v-model="email" type="email" :error.sync="emailError" required>
						Email Address
					</TextField>
					<div>
						<label class="field-label" for="Phone">Mobile Phone Number</label>
						<PhoneField v-model="phone" required />
						<small class="block mt-8 fs-12">We require a cell phone number so that we can notify you when there is action required for one of your jobs or job offers.</small>
					</div>
				</div>
				<p v-if="emailExists" class="email-exists-message">
					If this is your account, then try
					<router-link :to="{ name: $routes.Login.name }">logging in</router-link> or
					<router-link :to="{ name: $routes.ForgotPassword.name }">
						resetting your password </router-link>.
				</p>
			</fieldset>
			<fieldset class="form-section">
				<legend>Set Password</legend>
				<div class="form-row">
					<PasswordField v-model="password" hint="new" required :error="passwordError" @input="passwordError = null" aria-describedby="password-requirements">
						Password
					</PasswordField>
					<PasswordField v-model="confirmPassword" hint="new" required :error="confirmPasswordError" @input="confirmPasswordError = null">
						Confirm Password
					</PasswordField>
				</div>
				<p class="form-caption" id="password-requirements">
					{{ passwordMinLengthMessage }}
				</p>
			</fieldset>
		</template>
		<template #before-actions>
			<p class="onboarding-disclaimer">
				By clicking “{{ nextButtonText }}”, you agree to our
				<a href="https://shootwith.me/privacy-policy" target="_blank">Privacy Policy</a> and
				<a href="https://shootwith.me/terms-of-service" target="_blank">Terms of Use</a>.
			</p>
		</template>
	</OnboardingFormStep>
</template>

<script>
import { toString } from '@/helpers/convert';
import onboarding from '@/mixins/onboarding';

export default {
  name: 'OnboardingAccount',
  mixins: [onboarding],
  onboardingStep: {
    isApplicable: ({ user }) => !user,
    isCompleted: ({ user }) => user,
    requires: ({ plan }) => plan,
  },
  data() {
    return {
      phone: null,
      confirmPassword: null,
      confirmPasswordError: null,
      email: null,
      emailError: null,
      firstName: null,
      lastName: null,
      password: null,
      passwordError: null,
    };
  },
  computed: {
    /**
     * @returns {boolean}
     */
    emailExists() {
      return !!this.email && this.emailError === this.emailExistsErrorMessage;
    },
    /**
     * @returns {string}
     */
    emailExistsErrorMessage() {
      return 'An account already exists with this email address.';
    },
    /**
     * @returns {Intl.NumberFormat['format']}
     */
    formatNumber() {
      return this.$getNumberFormat().format;
    },
    /**
     * @returns {string}
     */
    nextButtonText() {
      return 'Create Free Account';
    },
    /**
     * @returns {number}
     */
    passwordMinLength() {
      return 8;
    },
    /**
     * @returns {string}
     */
    passwordMinLengthMessage() {
      const min = this.passwordMinLength;
      return `Your password must be at least ${min} character${min === 1 ? '' : 's'}.`;
    },
    /**
     * @returns {string}
     */
    planName() {
      return toString(this.plan?.name);
    },
  },
  methods: {
    /**
     * @returns {Promise<boolean | void>}
     */
    async commit() {
      const { confirmPassword, password } = this;
      if (password && password.length < this.passwordMinLength) {
        this.passwordError = this.passwordMinLengthMessage;
      }
      if (confirmPassword && confirmPassword !== password) {
        this.confirmPasswordError = "Your passwords don't match.";
      }
      if (this.passwordError || this.confirmPasswordError) {
        return false;
      }

      /** @type {(typeof this) & import('@/mixins/onboarding').OnboardingComponent} */
      const { email, firstName, lastName, plan, phone } = this;

      if (!plan) {
        this.$router.replace({ name: this.$routes.OnboardingRole.name });
        return false;
      }

      /** @type {CreateAccountPayload} */
      const payload = {
        email,
        firstName,
        lastName,
        password,
		phone,
		isSmsNotifyForUpdates: true,
        plan,
      };

      const store = this.$store;

      // Create account
      try {
        await store.dispatch('auth/createAccount', payload);
      } catch (error) {
        /** @type {readonly string[] | null | undefined} */
        const messages = error?.response?.data?.message;
        if (
          Array.isArray(messages) &&
          messages.length &&
          /^Email\s+(.*)\s*has\s+been\s+used/i.test(messages[0])
        ) {
          this.emailError = this.emailExistsErrorMessage;
          return false;
        }
        throw error;
      }
      store.commit('onboarding/setIsNewAccount', true);

      this.$gtmDispatchEvent('Account Created');
      this.$gtmDispatchOnboardingStepEvent(2, 'Account Created');
    },
  },
};
</script>
